<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { RoutePaths } from '@/Router/types/routePaths';
import { GuestService } from '@/api/guest/guestService';
import { FileDownloadService } from '@/services/fileDownload/fileDownloadService';
import { Guest, GuestClass, AdditionalQuestion } from '@/types/guest';
import GuestEditPopup from '@/components/GuestList/GuestEditPopup/GuestEditPopup.vue';
import GuestInfoPopup from '@/components/GuestList/GuestInfoPopup/GuestInfoPopup.vue';
import { AdditionalQuestionsService } from '@/api/guest/additionalQuestionsService';
import QuestionTemplatesPopup from '@/components/GuestList/QuestionTemplatesPopup/QuestionTemplatesPopup.vue';
import GuestStats from '@/components/GuestList/GuestStats/GuestStats.vue';
import GuestAnswersStats from '@/components/GuestList/GuestAnswersStats/GuestAnswersStats.vue';
import { useToast } from 'primevue/usetoast';
import LoadingSpinner from '@/components/Loading/LoadingSpinner.vue';
import { useRouter } from 'vue-router';

const abortController = ref(new AbortController());
const guests = ref<Guest[]>([]);
const editingGuest = ref<Guest | null>(null);
const dataLoading = ref(false);
const searchQuery = ref('');
const showAdditionalQuestions = ref(false);
const isSearchExpanded = ref(false);
const showEditPopup = ref(false);
const selectedGuestForQuestions = ref<Guest | null>(null);
const showQuestionTemplatesPopup = ref(false);

// Pagination state
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [5, 10, 20, 50];

const questionTemplates = ref<AdditionalQuestion[]>([]);

const filteredGuests = computed(() => {
  if (!searchQuery.value) return guests.value;

  const query = searchQuery.value.toLowerCase();
  const queryParts = query.match(/\S+\s*|\s+/g) || [];

  return guests.value.filter((guest) => {
    if (queryParts.length > 1) {
      return queryParts.every((part) => {
        const exactMatch = part.endsWith(' ');
        const searchTerm = exactMatch ? part.trim() : part;

        if (exactMatch) {
          return guest.name.toLowerCase() === searchTerm || guest.surname.toLowerCase() === searchTerm;
        }
        return guest.name.toLowerCase().includes(searchTerm) || guest.surname.toLowerCase().includes(searchTerm);
      });
    }

    const exactMatch = query.endsWith(' ');
    const searchTerm = exactMatch ? query.trim() : query;

    if (exactMatch) {
      return guest.name.toLowerCase() === searchTerm || guest.surname.toLowerCase() === searchTerm;
    }
    return guest.name.toLowerCase().includes(searchTerm) || guest.surname.toLowerCase().includes(searchTerm);
  });
});

const paginatedGuests = computed(() => {
  if (filteredGuests.value.length === 0) return [];
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredGuests.value.slice(start, end);
});

const totalPages = computed(() => {
  if (filteredGuests.value.length === 0) return 1;
  return Math.ceil(filteredGuests.value.length / itemsPerPage.value);
});

const editMode = computed(() => {
  if (!editingGuest.value) return 'add';
  return guests.value.some((g) => g.id === editingGuest.value?.id) ? 'edit' : 'add';
});

const getStatusIcon = (status: boolean | undefined) => {
  if (status === true) return 'pi pi-check-circle guest-list__status-icon guest-list__status-icon--confirmed';
  if (status === false) return 'pi pi-times-circle guest-list__status-icon guest-list__status-icon--declined';
  return 'pi pi-clock guest-list__status-icon guest-list__status-icon--pending';
};

const getStatusText = (status: boolean | undefined) => {
  if (status === true) return 'Potwierdził';
  if (status === false) return 'Odrzucił';
  return 'Oczekuje';
};

const formatAnswer = (answer: string | number | boolean | (string | number | boolean)[] | undefined): string => {
  if (answer === undefined || answer === null) return 'Brak odpowiedzi';
  if (typeof answer === 'boolean') return answer ? 'Tak' : 'Nie';
  if (Array.isArray(answer)) return answer.join(', ');
  return String(answer);
};

const formatGuestDataForExport = (guest: Guest) => {
  const baseData = {
    Imie: guest.name,
    Nazwisko: guest.surname,
    Status: getStatusText(guest.presence),
    'Numer stolu': guest.tableNumber ? guest.tableNumber.toString() : 'Nieprzypisany',
    'Ksztalt stolu':
      guest.tableLayoutInfo?.shape === 'round'
        ? 'Okragly'
        : guest.tableLayoutInfo?.shape === 'rectangular'
          ? 'Prostokatny'
          : 'Nieprzypisany',
    'Etykieta stolu': guest.tableLayoutInfo?.label || 'Brak',
  };

  // Add additional questions as separate columns
  const questionsData = guest.additionalQuestions.reduce(
    (acc, question) => {
      acc[`Pytanie ${question.id}`] = formatAnswer(question.answer);
      return acc;
    },
    {} as Record<string, string>,
  );

  return { ...baseData, ...questionsData };
};

const onDownloadGuestDataClick = (): void => {
  if (guests.value.length === 0) {
    alert('Brak danych do eksportu');
    return;
  }

  // Get all unique question texts for headers
  const allQuestionTexts = new Set<string>();
  guests.value.forEach((guest) => {
    guest.additionalQuestions.forEach((question) => {
      allQuestionTexts.add(`Pytanie ${question.id}`);
    });
  });

  // Create headers
  const headers = [
    'Imie',
    'Nazwisko',
    'Status',
    'Numer stolu',
    'Ksztalt stolu',
    'Etykieta stolu',
    ...Array.from(allQuestionTexts),
  ];

  // Format data for each guest
  const rows = guests.value.map((guest) => {
    const formattedData = formatGuestDataForExport(guest);
    return headers.map((header) => formattedData[header] || '');
  });

  // Create CSV content
  const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

  // Create and download file with BOM for proper UTF-8 encoding
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const fileName = `guests_${new Date().toISOString().split('T')[0]}.csv`;

  FileDownloadService.downloadFile({
    blob,
    fileName,
  });
};

const onAddGuest = (): void => {
  const newGuest = new GuestClass({
    id: uuidv4(),
    name: '',
    surname: '',
    presence: undefined,
    connection: '',
    connectedGuestId: '',
    additionalQuestions: [],
  });
  editingGuest.value = newGuest;
  showEditPopup.value = true;
};

const onEditGuest = (guest: Guest): void => {
  editingGuest.value = new GuestClass(guest);
  showEditPopup.value = true;
};

const toast = useToast();

const onSaveGuest = async (guest: Guest): Promise<void> => {
  try {
    dataLoading.value = true;
    const index = guests.value.findIndex((g) => g.id === guest.id);

    // Ensure all guests have the same question templates
    const updatedGuest = new GuestClass({
      ...guest,
      additionalQuestions: questionTemplates.value.map((template) => {
        const existingAnswer = guest.additionalQuestions.find((q) => q.id === template.id);
        return {
          id: template.id,
          answer: existingAnswer?.answer,
        };
      }),
    });

    if (index !== -1) {
      guests.value[index] = updatedGuest;
    } else {
      guests.value.unshift(updatedGuest);
    }

    await GuestService.updateGuestsData({
      abortSignal: abortController.value.signal,
      data: guests.value.map((guest) => ({
        id: guest.id,
        name: guest.name,
        surname: guest.surname,
        presence: guest.presence,
        connection: guest.connection,
        connectedGuestId: guest.connectedGuestId,
        additionalQuestions: guest.additionalQuestions,
        tableNumber: guest.tableNumber,
        tableLayoutInfo: guest.tableLayoutInfo,
        tableAdditionalInfo: guest.tableAdditionalInfo,
        seatIndex: guest.seatIndex,
      })),
    });

    toast.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Dane gościa zostały zapisane',
      life: 3000,
    });
  } catch (error) {
    console.error('Failed to save guest:', error);
    toast.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zapisać danych gościa. Spróbuj ponownie później.',
      life: 3000,
    });
  } finally {
    dataLoading.value = false;
    editingGuest.value = null;
    showEditPopup.value = false;
  }
};

const onDeleteGuest = async (guestId: string): Promise<void> => {
  if (confirm('Czy na pewno chcesz usunąć tego gościa?')) {
    try {
      dataLoading.value = true;
      guests.value = guests.value.filter((g) => g.id !== guestId);
      await GuestService.updateGuestsData({
        abortSignal: abortController.value.signal,
        data: guests.value.map((guest) => ({
          id: guest.id,
          name: guest.name,
          surname: guest.surname,
          presence: guest.presence,
          connection: guest.connection,
          connectedGuestId: guest.connectedGuestId,
          additionalQuestions: guest.additionalQuestions,
          tableNumber: guest.tableNumber,
          tableLayoutInfo: guest.tableLayoutInfo,
          tableAdditionalInfo: guest.tableAdditionalInfo,
          seatIndex: guest.seatIndex,
        })),
      });

      toast.add({
        severity: 'success',
        summary: 'Sukces',
        detail: 'Gość został usunięty',
        life: 3000,
      });
    } catch (error) {
      console.error('Failed to delete guest:', error);
      toast.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'Nie udało się usunąć gościa. Spróbuj ponownie później.',
        life: 3000,
      });
    } finally {
      dataLoading.value = false;
    }
  }
};

const onCancelEdit = (): void => {
  editingGuest.value = null;
  showEditPopup.value = false;
};

const handleSearchBlur = () => {
  if (!searchQuery.value) {
    isSearchExpanded.value = false;
  }
};

const onShowAdditionalQuestions = (guest: Guest): void => {
  selectedGuestForQuestions.value = guest;
  showAdditionalQuestions.value = true;
};

const onCloseAdditionalQuestions = (): void => {
  showAdditionalQuestions.value = false;
  selectedGuestForQuestions.value = null;
};

const onItemsPerPageChange = (value: number) => {
  itemsPerPage.value = value;
  currentPage.value = 1; // Reset to first page when changing items per page
};

const onPageChange = (page: number) => {
  currentPage.value = page;
};

// Add method to refresh question templates
const refreshQuestionTemplates = async () => {
  const newTemplates = AdditionalQuestionsService.getQuestionTemplates();

  // Only update if templates have actually changed
  if (JSON.stringify(newTemplates) !== JSON.stringify(questionTemplates.value)) {
    questionTemplates.value = newTemplates;

    // Update all guests with the new templates
    const updatedGuests = guests.value.map((guest) => {
      return new GuestClass({
        ...guest,
        additionalQuestions: newTemplates.map((template) => {
          const existingAnswer = guest.additionalQuestions.find((q) => q.id === template.id);
          return {
            id: template.id,
            answer: existingAnswer?.answer || '',
          };
        }),
      });
    });

    guests.value = updatedGuests;

    // Save the updated guests
    try {
      await GuestService.updateGuestsData({
        abortSignal: abortController.value.signal,
        data: updatedGuests.map((guest) => ({
          id: guest.id,
          name: guest.name,
          surname: guest.surname,
          presence: guest.presence,
          connection: guest.connection,
          connectedGuestId: guest.connectedGuestId,
          additionalQuestions: guest.additionalQuestions,
        })),
      });
    } catch (error) {
      console.error('Failed to update guests with new templates:', error);
    }
  }
};

// Add watcher for question templates with immediate: false to prevent initial trigger
watch(
  questionTemplates,
  () => {
    refreshQuestionTemplates();
  },
  { deep: true, immediate: false },
);

const router = useRouter();

const goBack = () => {
  router.back();
};

onMounted(async () => {
  dataLoading.value = true;
  try {
    // Load question templates
    const guestsData = await GuestService.getGuestsData();

    // Process and update templates based on guests data
    questionTemplates.value = AdditionalQuestionsService.processAndUpdateTemplates(guestsData);

    // Normalize guests with all expected questions
    guests.value = guestsData.map((guestData) => {
      const normalizedQuestions = AdditionalQuestionsService.normalizeGuestQuestions(
        guestData,
        questionTemplates.value,
      );

      return new GuestClass({
        ...guestData,
        additionalQuestions: normalizedQuestions,
      });
    });
  } catch (error) {
    console.error('Failed to load guests data:', error);
  } finally {
    dataLoading.value = false;
  }
});

onUnmounted(() => abortController.value.abort('Page changed...'));
</script>

<template>
  <div class="guest-list-page">
    <LoadingSpinner
      v-if="dataLoading"
      :background="'rgba(0, 0, 0, 0.2)'"
    />
    <div class="guest-list__header">
      <button
        class="guest-list__back-button"
        @click="goBack"
      >
        <i class="pi pi-arrow-left"></i>
        <span>Powrót</span>
      </button>
    </div>
    <GuestStats :guests="guests" />
    <GuestAnswersStats :guests="guests" />

    <div class="guest-list">
      <div class="guest-list__header">
        <div class="guest-list__search">
          <div
            class="guest-list__search-container"
            :class="{ 'guest-list__search-container--expanded': isSearchExpanded }"
          >
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Szukaj gości..."
              class="guest-list__search-input"
              @focus="isSearchExpanded = true"
              @blur="handleSearchBlur"
            />
            <button
              class="guest-list__search-button"
              @click="isSearchExpanded = !isSearchExpanded"
            >
              <i class="pi pi-search guest-list__search-icon"></i>
            </button>
          </div>
        </div>
        <div class="guest-list__actions">
          <button
            class="guest-list__action-button"
            :disabled="dataLoading"
            @click="onDownloadGuestDataClick"
          >
            <i class="pi pi-download"></i>
            Pobierz dane
          </button>
          <button
            class="guest-list__action-button guest-list__action-button--primary"
            :disabled="dataLoading"
            @click="onAddGuest"
          >
            <i class="pi pi-plus"></i>
            Dodaj gościa
          </button>
          <!-- <button
            class="guest-list__action-button"
            :disabled="dataLoading"
            @click="showQuestionTemplatesPopup = true"
          >
            <i class="pi pi-question-circle"></i>
            Zarządzaj pytaniami
          </button> -->
          <router-link
            :to="RoutePaths.guestsTable"
            class="guest-list__link"
          >
            <button
              class="guest-list__action-button guest-list__action-button--primary"
              :disabled="dataLoading"
            >
              Ustaw gości przy stołach
            </button>
          </router-link>
        </div>
      </div>

      <div class="guest-list__pagination-controls">
        <div class="guest-list__items-per-page">
          <span>Wyświetl:</span>
          <select
            v-model="itemsPerPage"
            @change="(e) => onItemsPerPageChange(Number((e.target as HTMLSelectElement).value))"
          >
            <option
              v-for="option in itemsPerPageOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>
        </div>
        <div class="guest-list__pagination">
          <button
            class="guest-list__pagination-button"
            :disabled="currentPage <= 1"
            @click="onPageChange(currentPage - 1)"
          >
            <i class="pi pi-chevron-left"></i>
          </button>
          <span class="guest-list__pagination-info"> Strona {{ currentPage }} z {{ totalPages }} </span>
          <button
            class="guest-list__pagination-button"
            :disabled="currentPage >= totalPages"
            @click="onPageChange(currentPage + 1)"
          >
            <i class="pi pi-chevron-right"></i>
          </button>
        </div>
      </div>

      <div class="guest-list__table-container">
        <table class="guest-list__table">
          <thead class="guest-list__table-header">
            <tr>
              <th class="guest-list__table-header-cell">
                <div class="guest-list__header-cell">
                  <i class="pi pi-user guest-list__header-icon"></i>
                  <span>Gość</span>
                </div>
              </th>
              <th class="guest-list__table-header-cell">
                <div class="guest-list__header-cell">
                  <i class="pi pi-info-circle guest-list__header-icon"></i>
                  <span>Status</span>
                </div>
              </th>
              <th class="guest-list__table-header-cell">
                <div class="guest-list__header-cell">
                  <i class="pi pi-comments guest-list__header-icon"></i>
                  <span>Dodatkowe informacje</span>
                </div>
              </th>
              <th class="guest-list__table-header-cell">
                <div class="guest-list__header-cell">
                  <i class="pi pi-cog guest-list__header-icon"></i>
                  <span>Akcje</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="guest-list__table-body">
            <tr
              v-for="guest in paginatedGuests"
              :key="guest.id"
              class="guest-list__table-row"
            >
              <td class="guest-list__table-cell">
                <span>{{ guest.name }} {{ guest.surname }}</span>
              </td>
              <td class="guest-list__table-cell">
                <div class="guest-list__status-value">
                  <i :class="getStatusIcon(guest.presence)"></i>
                  <span>{{ getStatusText(guest.presence) }}</span>
                </div>
              </td>
              <td class="guest-list__table-cell">
                <div class="guest-list__additional-questions">
                  <button
                    class="guest-list__toggle-questions"
                    @click="() => onShowAdditionalQuestions(guest)"
                  >
                    Pokaż odpowiedzi
                  </button>
                </div>
              </td>
              <td class="guest-list__table-cell">
                <div class="guest-list__actions">
                  <button
                    class="guest-list__action-button"
                    :disabled="dataLoading"
                    @click="() => onEditGuest(guest)"
                  >
                    <i class="pi pi-pencil"></i>
                    Edytuj
                  </button>
                  <button
                    class="guest-list__action-button guest-list__action-button--danger"
                    :disabled="dataLoading"
                    @click="() => onDeleteGuest(guest.id)"
                  >
                    <i class="pi pi-trash"></i>
                    Usuń
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <GuestEditPopup
        v-if="showEditPopup && editingGuest"
        :guest="editingGuest"
        :mode="editMode"
        :question-templates="questionTemplates"
        @save="onSaveGuest"
        @cancel="onCancelEdit"
      />
      <GuestInfoPopup
        v-if="showAdditionalQuestions && selectedGuestForQuestions"
        :guest="selectedGuestForQuestions"
        :is-visible="showAdditionalQuestions"
        @close="onCloseAdditionalQuestions"
      />
      <QuestionTemplatesPopup
        :is-visible="showQuestionTemplatesPopup"
        @close="showQuestionTemplatesPopup = false"
        @question-added="refreshQuestionTemplates"
        @question-deleted="refreshQuestionTemplates"
      />
    </div>
    <Toast position="bottom-right" />
  </div>
</template>

<style lang="scss" scoped>
.guest-list-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
}

.guest-list {
  padding: 2rem;
  min-height: 30rem;

  &__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;
    flex-wrap: wrap-reverse;
    gap: 1rem;
  }

  &__back-button {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.5rem;
    background-color: $brown-200;
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-size: 1.4rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba($black-100, 0.1);

    @media (max-width: $media-max-phone) {
      padding: 0.5rem 1rem;
      font-size: 1.2rem;
    }

    &:hover {
      background-color: $brown-100;
      color: $white;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba($black-100, 0.15);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba($black-100, 0.1);
    }

    i {
      font-size: 1.4rem;

      @media (max-width: $media-max-phone) {
        font-size: 1.2rem;
      }
    }

    span {
      font-size: 1.4rem;
      font-weight: 600;

      @media (max-width: $media-max-phone) {
        font-size: 1.2rem;
      }
    }
  }

  &__search {
    flex: 1;
    min-width: 200px;
    position: relative;
  }

  &__search-container {
    display: flex;
    align-items: center;
    background-color: #f2e9e4;
    border: 1px solid $brown-300;
    border-radius: 4px;
    transition: all 0.3s ease;
    width: 120px;
    overflow: hidden;
    height: 40px;

    &--expanded {
      width: 100%;
      background-color: $white;
    }
  }

  &__search-input {
    flex: 1;
    padding: 0 0.75rem;
    border: none;
    font-size: 1.3125rem;
    background: transparent;
    transition: all 0.3s ease;
    height: 100%;
    color: #834920;

    &::placeholder {
      color: #834920;
      opacity: 1;
      font-weight: 600;
    }

    &:focus {
      outline: none;
    }
  }

  &__search-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.75rem;
    background: none;
    border: none;
    cursor: pointer;
    color: #834920;
    transition: color 0.3s ease;
    height: 100%;
    min-width: 40px;

    &:hover {
      color: $brown-200;
    }
  }

  &__search-icon {
    font-size: 1.5rem;
  }

  &__actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;

    @media (max-width: $media-max-phone) {
      flex-direction: column;
      width: 100%;
    }
  }

  &__link {
    text-decoration: none;
    display: inline-flex;

    @media (max-width: $media-max-phone) {
      width: 100%;
    }
  }

  &__table-container {
    overflow-x: auto;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba($black, 0.1);
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    background-color: $white;
  }

  &__table-header {
    background-color: $brown-600;
  }

  &__table-header-cell {
    padding: 1rem;
    text-align: left;
    font-weight: bold;
    color: $black-300;
    border-bottom: 2px solid $brown-300;
  }

  &__table-body {
    tr:nth-child(even) {
      background-color: $white-200;
    }
  }

  &__table-row {
    &:hover {
      background-color: $brown-450;
    }
  }

  &__table-cell {
    padding: 1rem;
    border-bottom: 1px solid $brown-300;
  }

  &__input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid $brown-300;
    border-radius: 4px;
    background-color: $white;
    color: $black-300;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: $brown-200;
    }
  }

  &__checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__checkbox {
    display: none;
  }

  &__checkbox-custom {
    width: 1.2rem;
    height: 1.2rem;
    border: 2px solid $brown-300;
    border-radius: 4px;
    transition: all 0.3s ease;
  }

  &__checkbox:checked + .guest-list__checkbox-custom {
    background-color: $brown-200;
    border-color: $brown-200;
  }

  &__additional-questions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__toggle-questions {
    padding: 0.5rem;
    background: none;
    border: none;
    color: $brown-200;
    cursor: pointer;
    text-align: left;
    transition: color 0.3s ease;

    &:hover {
      color: $brown-100;
    }
  }

  &__header-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__header-icon {
    font-size: 1.2rem;
    color: $black-300;
  }

  &__name-inputs {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__status-dropdown {
    width: 100%;
  }

  &__status-value,
  &__status-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      font-size: 1.2rem;
    }
  }

  &__action-button {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.5rem;
    border: 1px solid $brown-300;
    border-radius: 0.5rem;
    background-color: $brown-200;
    color: $white;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 1.68rem;
    box-shadow: 0 2px 4px rgba($black-100, 0.1);

    @media (max-width: $media-max-phone) {
      padding: 0.5rem 1rem;
      font-size: 1.5rem;
      flex: 1;
      justify-content: center;
    }

    &:hover {
      background-color: $brown-100;
      color: $white;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba($black-100, 0.15);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba($black-100, 0.1);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--primary {
      background-color: $brown-200;
      color: $white;
      border-color: $brown-200;

      &:hover {
        background-color: $brown-100;
        border-color: $brown-100;
      }

      &:disabled {
        background-color: $brown-300;
        border-color: $brown-300;
      }
    }

    &--danger {
      background-color: #dc3545;
      color: $white;
      border-color: #dc3545;

      &:hover {
        background-color: #c82333;
        border-color: #c82333;
      }

      &:disabled {
        background-color: #e4606d;
        border-color: #e4606d;
        color: $white;
      }
    }

    i {
      font-size: 1.8rem;
    }
  }

  &__action-button--delete {
    color: $brown-700;

    &:hover {
      background-color: $brown-450;
      border-color: $brown-700;
    }
  }

  @media (max-width: $media-max-phone) {
    padding: 1rem;

    &__actions {
      flex-direction: column;
      width: 100%;
    }

    &__table-header-cell {
      padding: 0.5rem;
      font-size: 0.9rem;
    }

    &__table-cell {
      padding: 0.5rem;
    }
  }

  &__pagination-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: $white;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba($black, 0.1);
  }

  &__items-per-page {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    select {
      padding: 0.25rem 0.5rem;
      border: 1px solid $brown-300;
      border-radius: 4px;
      background-color: $white;
      color: $black-300;
      cursor: pointer;
    }
  }

  &__pagination {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__pagination-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 1px solid $brown-300;
    border-radius: 4px;
    background-color: $white;
    color: $brown-200;
    cursor: pointer;
    transition: all 0.2s ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background-color: $brown-200;
      color: $white;
    }
  }

  &__pagination-info {
    color: $black-300;
    font-size: 1.35rem;
  }

  &__status-icon {
    font-size: 1.2rem;

    &--confirmed {
      color: green;
    }

    &--declined {
      color: red;
    }

    &--pending {
      color: $brown-300;
    }
  }
}
</style>
